import { inject, Injectable } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AttachmentEditModalComponent } from '../components/attachments/attachment-edit-modal/attachment-edit-modal.component';
import { AttachmentUploadDetailsModalComponent } from '../components/attachments/attachment-upload-details-modal/attachment-upload-details-modal.component';
import { AttachmentUploadFailureModalComponent } from '../components/attachments/attachment-upload-failure-modal/attachment-upload-failure-modal.component';
import { AttachmentUploadModalComponent } from '../components/attachments/attachment-upload-modal/attachment-upload-modal.component';
import { AttachmentUploadSuccessModalComponent } from '../components/attachments/attachment-upload-success-modal/attachment-upload-success-modal.component';
import { ConfirmModalComponent } from '../components/confirm-modal/confirm-modal.component';
import { ConfirmSaleModalComponent } from '../components/confirm-sale-modal/confirm-sale-modal.component';
import { EditDispositionModalComponent } from '../components/edit-disposition-modal/edit-disposition-modal.component';
import { ErrorStateModalComponent } from '../components/error-state-modal/error-state-modal.component';
import { ManualPostPaymentModalComponent } from '../components/manual-post-payment-modal/manual-post-payment-modal.component';
import { RemoveFromFollowUpModalComponent } from '../components/remove-from-follow-up-modal/remove-from-follow-up-modal.component';
import { SendDepositRequestModalComponent } from '../components/send-deposit-request-modal/send-deposit-request-modal.component';
import { SuccessfulOutcomeModalComponent } from '../components/successful-outcome-modal/successful-outcome-modal.component';
import { AppointmentHistoryItem } from '../models/appointment-history-item.model';
import { QuoteInfo } from '../sdk/sdk';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly ngbModal = inject(NgbModal);

  openConfirmSaleModal(opportunityId: string, quote: QuoteInfo): NgbModalRef {
    const modalRef = this.ngbModal.open(ConfirmSaleModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.opportunityId = opportunityId;
    modalRef.componentInstance.quote = quote;

    return modalRef;
  }

  openRemoveFromFollowUpModal(opportunityId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(RemoveFromFollowUpModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.opportunityId = opportunityId;

    return modalRef;
  }

  openErrorModal(title: string, message: string): NgbModalRef {
    const modalRef = this.ngbModal.open(ErrorStateModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.alertMessage = message;

    return modalRef;
  }

  openSuccessModal(title: string, message: string): NgbModalRef {
    const modalRef = this.ngbModal.open(SuccessfulOutcomeModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.successMessage = message;

    return modalRef;
  }

  openAttachmentUploadModal(): NgbModalRef {
    return this.ngbModal.open(AttachmentUploadModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
    });
  }

  openAttachmentUploadDetailsModal(
    files: File[],
    opportunityId: string,
    appointmentId: string,
    quoteId: string,
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AttachmentUploadDetailsModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
    });

    modalRef.componentInstance.files = files;
    modalRef.componentInstance.opportunityId = opportunityId;
    modalRef.componentInstance.appointmentId = appointmentId;
    modalRef.componentInstance.quoteId = quoteId;
    return modalRef;
  }

  openAttachmentEditModal(
    attachmentId: string,
    initialGroupId: string | null,
    initialDescription: string,
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AttachmentEditModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.attachmentId = attachmentId;
    modalRef.componentInstance.initialGroupId = initialGroupId;
    modalRef.componentInstance.initialDescription = initialDescription;
    return modalRef;
  }

  openAttachmentUploadSuccessModal(): NgbModalRef {
    return this.ngbModal.open(AttachmentUploadSuccessModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
  }

  openAttachmentUploadFailureModal(
    failedFiles: File[],
    opportunityId: string,
    appointmentId: string,
    groupId: string | null,
    description: string,
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(AttachmentUploadFailureModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.failedFiles = failedFiles;
    modalRef.componentInstance.opportunityId = opportunityId;
    modalRef.componentInstance.appointmentId = appointmentId;
    modalRef.componentInstance.groupId = groupId;
    modalRef.componentInstance.description = description;
    return modalRef;
  }

  openConfirmModal(
    title: string,
    message: string,
    confirmButtonText = 'Confirm',
    cancelButtonText = 'Cancel',
    alertClasses = 'alert alert-warning',
  ): NgbModalRef {
    const modalRef = this.ngbModal.open(ConfirmModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });

    modalRef.componentInstance.title = title;
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.confirmButtonText = confirmButtonText;
    modalRef.componentInstance.cancelButtonText = cancelButtonText;
    modalRef.componentInstance.alertClasses = alertClasses;

    return modalRef;
  }

  openEditDispositionModal(appointment: AppointmentHistoryItem): NgbModalRef {
    const modalRef = this.ngbModal.open(EditDispositionModalComponent, {
      backdrop: 'static',
      keyboard: false,
      size: 'md',
      centered: true,
    });

    modalRef.componentInstance.appointment = appointment;

    return modalRef;
  }

  openSendDepositRequestModal(opportunityId: string, customerId: number): NgbModalRef {
    const modalRef = this.ngbModal.open(SendDepositRequestModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
    });

    modalRef.componentInstance.opportunityId = opportunityId;
    modalRef.componentInstance.customerId = customerId;

    return modalRef;
  }

  openManualPostPaymentModal(opportunityId: string): NgbModalRef {
    const modalRef = this.ngbModal.open(ManualPostPaymentModalComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
    });

    modalRef.componentInstance.opportunityId = opportunityId;

    return modalRef;
  }

  dismissAll(): void {
    this.ngbModal.dismissAll();
  }
}
